import { useState, useMemo, useCallback } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import { deleteUserById } from '../../Services/serviceURL';
import { setCurrentUserId } from './usersSlice';
import useSearch from '../../hooks/useSearch';
import { toast } from 'react-toastify';
import type { AddUserFormData, CustomerData, CustomerLite, FormErrors, SalesDistrict, SelectionMap } from './addUserTypes';
import { validateAddUserForm } from './addUserValidation';
import { extractSaveUserErrorMessage, persistAddUser } from './addUserSave';
import { buildExpandedItemsForCustomers, filterCustomersBySearch } from './addUserCustomerSearch';
import {
    buildCustomerSelectState,
    buildPlantSelectState,
    buildSelectAllState,
    buildSiteSelectState,
    getTotalPlantCount,
} from './addUserSelection';
import {
    applySsoToggleChange,
    clearFieldErrorIfPresent,
    resolveAddUserFormDataChange,
    shouldResetPasswordDisplayOnInput,
} from './addUserFormInput';
import { useAddUserPageEffects } from './useAddUserPageEffects';

const initialFormData: AddUserFormData = {
    userType: 'Internal',
    email: '',
    password: '',
    ssoEnabled: false,
    firstName: '',
    lastName: '',
    mobilePhoneCode: '',
    mobilePhone: '',
    officePhoneCode: '',
    officePhone: '',
    role: 'User',
    status: 'Enabled',
    salesDistrict: '',
    salesRegion: '',
};

const initialErrors: FormErrors = {
    email: '',
    password: '',
    firstName: '',
    lastName: '',
    customerId: '',
};

export function useAddUserPage() {
    const navigate = useNavigate();
    const dispatch = useDispatch();
    const { userId } = useParams<{ userId: string }>();
    const isEditMode = Boolean(userId);

    const [customersAndSites, setCustomersAndSites] = useState<CustomerData[]>([]);
    const [selectedItems, setSelectedItems] = useState<SelectionMap>({});
    const [expandedItems, setExpandedItems] = useState<SelectionMap>({});
    const [customers, setCustomers] = useState<CustomerLite[]>([]);
    const [formData, setFormData] = useState<AddUserFormData>(initialFormData);
    const [searchText, setSearchText] = useState('');
    const [filteredCustomers, setFilteredCustomers] = useState<CustomerData[]>([]);
    const [errors, setErrors] = useState<FormErrors>(initialErrors);
    const [loading, setLoading] = useState(false);
    const [loadingUserData, setLoadingUserData] = useState(false);
    const [userDataLoaded, setUserDataLoaded] = useState(false);
    const [districts, setDistricts] = useState<SalesDistrict[]>([]);
    const [loadingDistricts, setLoadingDistricts] = useState(false);
    const [, setErrorDistricts] = useState<string | null>(null);
    const [showDeleteModal, setShowDeleteModal] = useState(false);
    const [userToDelete, setUserToDelete] = useState<string | null>(null);
    const [showEncryptedPassword, setShowEncryptedPassword] = useState(false);
    const [showPassword, setShowPassword] = useState(false);
    const { filteredData: filteredCustomersFromHook } = useSearch(customersAndSites);

    useAddUserPageEffects({
        isEditMode,
        userId,
        userDataLoaded,
        setUserDataLoaded,
        setLoadingUserData,
        setFormData,
        setShowEncryptedPassword,
        setSelectedItems,
        setCustomers,
        setCustomersAndSites,
        filteredCustomersFromHook,
        setFilteredCustomers,
        customersAndSites,
        setDistricts,
        setLoadingDistricts,
        setErrorDistricts,
        formData,
        isEditModeForExternalReset: isEditMode,
        setExpandedItems,
    });

    const visibleCustomers = useMemo(() => {
        if (formData.userType === 'External' && formData.customerId) {
            return customersAndSites.filter(c => c.customerId === formData.customerId);
        }
        return customersAndSites;
    }, [formData.userType, formData.customerId, customersAndSites]);

    const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
        const { name, value } = e.target;
        const cleared = clearFieldErrorIfPresent(errors, name);
        if (cleared) {
            setErrors(cleared);
        }

        if (shouldResetPasswordDisplayOnInput(name)) {
            setShowEncryptedPassword(false);
            setShowPassword(false);
        }

        const update = resolveAddUserFormDataChange(name, value, formData, districts);
        if (update.kind === 'set') {
            setFormData(update.formData);
        }
    };

    const setUserType = (userType: 'Internal' | 'External') => {
        handleInputChange({ target: { name: 'userType', value: userType } } as React.ChangeEvent<HTMLSelectElement>);
    };

    const setSsoEnabled = (enabled: boolean) => {
        setShowEncryptedPassword(false);
        setShowPassword(false);
        setFormData(prev => applySsoToggleChange(prev, enabled));
    };

    const validateForm = () => {
        const { isValid, errors: nextErrors } = validateAddUserForm(formData, isEditMode);
        setErrors(nextErrors);
        return isValid;
    };

    const handleSave = async (e: React.FormEvent) => {
        e.preventDefault();
        if (!validateForm()) {
            return;
        }

        try {
            setLoading(true);
            const result = await persistAddUser(formData, selectedItems, customersAndSites, isEditMode, userId);

            if (result.outcome === 'success') {
                if (result.savedUserId) {
                    dispatch(setCurrentUserId(result.savedUserId));
                }
                toast.success(`User ${isEditMode ? 'updated' : 'saved'} successfully`);
                setTimeout(() => navigate('/users'), 2000);
                return;
            }

            toast.error(result.message);
            if (result.emailAlreadyExists) {
                setErrors(prev => ({ ...prev, email: 'This email address is already in use' }));
            }
        } catch (error: unknown) {
            toast.error(extractSaveUserErrorMessage(error, isEditMode));
        } finally {
            setLoading(false);
        }
    };

    const toggleExpand = (id: string) => {
        setExpandedItems(prev => ({ ...prev, [id]: !prev[id] }));
    };

    const handleSelectAll = (checked: boolean) => {
        setSelectedItems(buildSelectAllState(customersAndSites, selectedItems, checked));
    };

    const handleCustomerSelect = (customerId: string, checked: boolean) => {
        setSelectedItems(buildCustomerSelectState(customersAndSites, selectedItems, customerId, checked));
    };

    const handleSiteSelect = (siteId: string, customerId: string, checked: boolean) => {
        setSelectedItems(buildSiteSelectState(customersAndSites, selectedItems, siteId, customerId, checked));
    };

    const handlePlantSelect = (plantId: string, siteId: string, customerId: string, checked: boolean) => {
        setSelectedItems(buildPlantSelectState(customersAndSites, selectedItems, plantId, siteId, customerId, checked));
    };

    const handleSearch = (searchValue: string) => {
        setSearchText(searchValue);
        const filtered = filterCustomersBySearch(customersAndSites, searchValue);
        setFilteredCustomers(filtered);
        setExpandedItems(buildExpandedItemsForCustomers(filtered, expandedItems));
    };

    const handleDelete = (deleteUserId: string) => {
        setUserToDelete(deleteUserId);
        setShowDeleteModal(true);
    };

    const confirmDelete = async () => {
        if (!userToDelete) {
            return;
        }
        try {
            const response = await deleteUserById(userToDelete);
            if (response.Success) {
                setShowDeleteModal(false);
                setUserToDelete(null);
                navigate('/users', {
                    state: { userDeleted: true, message: 'User deleted successfully' },
                });
                return;
            }
            toast.error('Failed to delete user');
        } catch {
            toast.error('An error occurred while deleting user');
        }
        setShowDeleteModal(false);
        setUserToDelete(null);
    };

    const cancelDelete = () => {
        setShowDeleteModal(false);
        setUserToDelete(null);
    };

    const isCustomerSelected = useCallback((customerId: string) => selectedItems[customerId] || false, [selectedItems]);
    const isSiteSelected = useCallback((siteId: string) => selectedItems[siteId] || false, [selectedItems]);
    const isPlantSelected = useCallback((plantId: string) => selectedItems[plantId] || false, [selectedItems]);

    const displayCustomers = searchText ? filteredCustomers : visibleCustomers;
    const isOverlayLoading = loading || loadingUserData || loadingDistricts;

    return {
        navigate,
        userId,
        isEditMode,
        customers,
        formData,
        errors,
        loading,
        loadingUserData,
        showDeleteModal,
        showEncryptedPassword,
        showPassword,
        setShowPassword,
        searchText,
        displayCustomers,
        expandedItems,
        isOverlayLoading,
        handleInputChange,
        setUserType,
        setSsoEnabled,
        handleSave,
        toggleExpand,
        handleSelectAll,
        handleCustomerSelect,
        handleSiteSelect,
        handlePlantSelect,
        handleSearch,
        handleDelete,
        confirmDelete,
        cancelDelete,
        isCustomerSelected,
        isSiteSelected,
        isPlantSelected,
        getTotalCount: () => getTotalPlantCount(customersAndSites),
    };
}
